import { useMemo, useRef } from "react";
import type { Editor } from "tldraw";

import { useDrawSurface } from "./useDrawSurface";
import { rectContainsPoint } from "./geometry";
import { markDrawRegion } from "./shield";
import { inkForNextShape } from "./inkForNextShape";
import type { DrawSurfaceAdapter } from "./types";

/**
 * tldraw already listens for `pointermove` on document.body regardless of
 * button state, so a hovering finger is *already* feeding `pointer_move` into
 * its state chart. Only the press needs synthesizing: `pointer_down` puts the
 * draw tool into Drawing, and hover moves extend the stroke until `pointer_up`.
 *
 * `dispatch` flushes synchronously for pointer_down/up, so release is
 * immediate — which matters when a submission must capture an in-flight stroke.
 */

/** Kept away from real pointer ids, which start at 1. */
const SYNTHETIC_POINTER_ID = 9_999_001;

/**
 * Transparent overlay mounted while the lock owns the stroke: stray clicks hit
 * the shield instead of tldraw, whose pointer_up would otherwise end the
 * synthetic stroke. Hover moves keep drawing — tldraw reads pointermove off
 * document.body, which the shield doesn't block.
 */
export function mountPointerShield(
  container: HTMLElement,
  ink: string,
): () => void {
  const shield = container.ownerDocument.createElement("div");
  // The overlay is a draw-cursor region in the ink of the stroke it guards.
  markDrawRegion(shield, ink);
  Object.assign(shield.style, {
    position: "absolute",
    inset: "0",
    zIndex: "9999",
    touchAction: "none",
    userSelect: "none",
  });
  // The one press default an inert overlay can't absorb by existing.
  shield.addEventListener("contextmenu", (e) => e.preventDefault());
  container.appendChild(shield);
  return () => {
    shield.remove();
  };
}

function pointerInfo(x: number, y: number) {
  return {
    type: "pointer" as const,
    target: "canvas" as const,
    // A hovering pointer reports pressure 0; tldraw reads `z` as pressure.
    point: { x, y, z: 0.5 },
    shiftKey: false,
    altKey: false,
    ctrlKey: false,
    metaKey: false,
    accelKey: false,
    pointerId: SYNTHETIC_POINTER_ID,
    button: 0,
    isPen: false,
  };
}

interface UseTldrawDrawLockArgs {
  surfaceId: string;
  /** Read lazily — the editor mounts after this hook runs, and remounts. */
  getEditor: () => Editor | null;
  isActive: boolean;
}

export function useTldrawDrawLock({
  surfaceId,
  getEditor,
  isActive,
}: UseTldrawDrawLockArgs): void {
  const getEditorRef = useRef(getEditor);
  getEditorRef.current = getEditor;

  const previousToolIdRef = useRef<string | null>(null);
  const unmountShieldRef = useRef<(() => void) | null>(null);

  const adapter = useMemo<DrawSurfaceAdapter>(
    () => ({
      id: surfaceId,
      canEngage: () => {
        const editor = safeEditor(getEditorRef.current);
        if (!editor) return false;
        // UX guard only — the server remains the authority on writes.
        if (editor.getInstanceState().isReadonly) return false;
        if (editor.getEditingShapeId()) return false;
        if (editor.inputs.getIsPointing()) return false;
        return true;
      },
      containsPoint: (point) => {
        const editor = safeEditor(getEditorRef.current);
        return rectContainsPoint(
          editor?.getContainer()?.getBoundingClientRect(),
          point,
        );
      },
      // The container wraps the entire editor UI, toolbar and style panel
      // included.
      ownsElement: (element) =>
        safeEditor(getEditorRef.current)?.getContainer()?.contains(element) ??
        false,
      engage: ({ x, y }) => {
        const editor = safeEditor(getEditorRef.current);
        if (!editor) return false;
        previousToolIdRef.current = editor.getCurrentToolId();
        editor.setCurrentTool("draw");
        editor.dispatch({ ...pointerInfo(x, y), name: "pointer_down" });
        const container = editor.getContainer();
        if (container) {
          unmountShieldRef.current = mountPointerShield(
            container,
            inkForNextShape(editor),
          );
        }
        return true;
      },
      release: (_reason, cancel) => {
        unmountShieldRef.current?.();
        unmountShieldRef.current = null;
        const editor = safeEditor(getEditorRef.current);
        const previousToolId = previousToolIdRef.current;
        previousToolIdRef.current = null;
        if (!editor) return;

        if (cancel) {
          // Discard the in-progress shape rather than committing it.
          editor.cancel();
        } else {
          const { x, y } = editor.inputs.getCurrentScreenPoint();
          const bounds = editor.getViewportScreenBounds();
          // getCurrentScreenPoint is viewport-relative and will be adjusted
          // again on the way in, so counter-adjust before dispatching.
          editor.dispatch({
            ...pointerInfo(x + bounds.x, y + bounds.y),
            name: "pointer_up",
          });
        }

        if (previousToolId && previousToolId !== editor.getCurrentToolId()) {
          try {
            editor.setCurrentTool(previousToolId);
          } catch {
            editor.setCurrentTool("select");
          }
        }
      },
    }),
    [surfaceId],
  );

  useDrawSurface(adapter, isActive);
}

/** The editor getter throws once the underlying agent app is disposed. */
function safeEditor(get: () => Editor | null): Editor | null {
  try {
    return get();
  } catch {
    return null;
  }
}
